import CircularProgress from "@mui/material/CircularProgress";
import { Modal } from "@mui/material";
import { UserContext } from "App";
import { debounce } from "lodash";
import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  FilterProduct,
  getPopularProducts,
} from "../../../../../../../../config/Services";
import { useSnackbar } from "../../../../../hooks/SnackbarProvider";
import KeyboardDropDownModal from "../../../keyboardDropDownModal";
import BundleBuilder from "../BundleBuilder";
import BundleList from "../BundleList";
import SearchSidebar from "./SearchSidebar";
import ProductGrid from "./ProductGrid";
import "./index.css";
import { customEventTrigger } from "../../../../../../../../analytics";

/* ── localStorage helpers (mirror of BundleBuilder) ── */
const BUNDLES_KEY = "pallet_saved_bundles";
const loadSavedBundles = () => {
  try {
    return JSON.parse(localStorage.getItem(BUNDLES_KEY) || "[]");
  } catch {
    return [];
  }
};
const persistBundles = (list) => localStorage.setItem(BUNDLES_KEY, JSON.stringify(list));

const SearchBar = (props) => {
  const showSnackbar = useSnackbar();
  const [data, setData] = useState([]);
  const { color, searchText, setSearchText } = useContext(UserContext);
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const [loader, setLoader] = useState(false);
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - 1);
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;
  const [recentData, setResendData] = useState([]);
  const [listType, setListType] = useState("");
  const searchRef = useRef(null);
  const retailType = localStorage.getItem("retailType");
  const [showBundleBuilder, setShowBundleBuilder] = useState(false);
  const [showBundleList, setShowBundleList] = useState(false);

  /* ── left-pane navigation selection ── */
  // { type: "top-searches" } | { type: "bundles" } | { type: "category", name: string } | null
  const [leftSelection, setLeftSelection] = useState(null);
  const [leftPaneData, setLeftPaneData] = useState([]);
  const [leftPaneLoading, setLeftPaneLoading] = useState(false);

  /* ── saved bundles state ── */
  const [savedBundles, setSavedBundles] = useState([]);
  const [bundleInitialData, setBundleInitialData] = useState(null);

  const refreshSavedBundles = () => setSavedBundles(loadSavedBundles());

  /* Focus the first button in the sidebar — called from product ADD buttons via ArrowLeft */
  const focusSidebar = useCallback(() => {
    const firstBtn = document.querySelector(".ss-pane button");
    firstBtn?.focus();
  }, []);

  /* load saved bundles whenever the drawer opens */
  useEffect(() => {
    if (props.searchOpen) refreshSavedBundles();
  }, [props.searchOpen]);

  /* Auto-select top-searches tab and focus search bar whenever the drawer opens */
  useEffect(() => {
    if (props.searchOpen) {
      setLeftSelection({ type: "top-searches" });
      setLeftPaneData(recentData || []);
      setTimeout(() => searchRef?.current?.focus(), 50);
    }
  }, [props.searchOpen]);

  useEffect(() => {
    getPopularProducts(formattedDate, locId).then((response) => {
      if (response?.data?.es == 2 && response?.data?.message == "No data found!") {
        setData([]);
        setListType("search");
        return;
      }

      /* Extract GTINs from the metrics response */
      const metricsProducts = response?.data?.products || [];
      const gtins = metricsProducts
        .map((p) => p?.gtin || p?.barcode || p?.productGtin || p?.ean)
        .filter(Boolean);

      if (gtins.length === 0) {
        setData([]);
        setListType("search");
        return;
      }

      /* Fetch full product details (with inventory sync data) from CMS */
      FilterProduct({
        storeLocations: [locId],
        storeIds: [orgId],
        productTypes: [],
        page: 1,
        pageSize: gtins.length,
        query: "",
        foodType: [],
        salesChannels: [],
        barcode: gtins,
        flattenResponse: true,
      })
        .then((cmsRes) => {
          const cmsProducts = cmsRes?.data?.data?.data?.data || [];
          setData(cmsProducts);
          setResendData(cmsProducts);
          setListType("recent");
          /* Update top-searches pane if it's currently selected */
          setLeftPaneData((prev) =>
            prev === recentData || prev?.length === 0 ? cmsProducts : prev
          );
        })
        .catch(() => {
          setData([]);
          setListType("search");
        });
    });
    setData([]);
  }, [props.searchOpen]);

  const clickPress = (value) => {
    setSearchText(value);

    const payload = {
      page: "1",
      pageSize: "100",
      query: value,
      storeLocations: [locId],
      storeIds: [orgId],
      flattenResponse: true,
      ...(retailType == "GROCERY" && { bundleIncluded: false }),
    };

    setLoader(true);
    FilterProduct(payload)
      .then((res) => {
        setLoader(false);
        setListType("search");
        if (res?.data?.data?.data?.totalRecords === "0") {
          setData([]);
        } else {
          setData(res?.data?.data?.data?.data);
        }
      })
      .catch((err) => {
        setLoader(false);
        showSnackbar(err?.message || err?.data?.response?.message, "error");
      });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setSearchText("");
        setData([]);
        setShowBundleBuilder(false);
        setShowBundleList(false);
        setBundleInitialData(null);
        setLeftSelection(null);
        setLeftPaneData([]);
        props?.closeSearchProductModal();
      }
      if (e.key === "Delete") {
        clearSearchText();
      }
      if (e.key === "`") {
        searchRef?.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [data, listType]);

  const clearSearchText = () => {
    setSearchText("");
    setData([]);
    setLeftSelection({ type: "top-searches" });
    setLeftPaneData(recentData || []);
  };

  /* ── Left-pane nav selection ── */
  const handleLeftSelect = (sel) => {
    // Close bundle builder/list if open
    setShowBundleBuilder(false);
    setShowBundleList(false);
    setBundleInitialData(null);
    // Clear search text
    setSearchText("");
    setData([]);
    // Keep focus in search bar so user can type immediately
    setTimeout(() => searchRef?.current?.focus(), 30);

    if (sel.type === "bundles") {
      setLeftSelection({ type: "bundles" });
      setLeftPaneData([]);
      return;
    }

    if (sel.type === "top-searches") {
      setLeftSelection({ type: "top-searches" });
      setLeftPaneData(recentData || []);
      return;
    }

    if (sel.type === "category") {
      setLeftSelection({ type: "category", name: sel.name });
      setLeftPaneData([]);
      setLeftPaneLoading(true);
      FilterProduct({
        page: 1,
        pageSize: 30,
        appCategories: { categoryLevel1: [sel.name] },
        storeLocations: [locId],
        storeIds: [orgId],
        flattenResponse: true,
      })
        .then((res) => setLeftPaneData(res?.data?.data?.data?.data || []))
        .catch(() => setLeftPaneData([]))
        .finally(() => setLeftPaneLoading(false));
    }
  };

  const debouncedSearch = useCallback(
    debounce((nextValue) => {
      customEventTrigger("pos_search_product", { value: nextValue });
      clickPress(nextValue);
    }, 500),
    []
  );

  const handleChange = (event) => {
    const { value: nextValue } = event?.target;
    setSearchText(nextValue);
    // typing overrides any left-pane selection
    if (nextValue) {
      setLeftSelection(null);
      setLeftPaneData([]);
    }
    debouncedSearch(nextValue);
  };

  const handleBundleBuilderClose = () => {
    setShowBundleBuilder(false);
    setBundleInitialData(null);
  };

  const handleBundleAddedToCart = () => {
    setShowBundleBuilder(false);
    setBundleInitialData(null);
    setSearchText("");
    setData([]);
    props?.closeSearchProductModal();
  };

  const handleDrawerClose = () => {
    clearSearchText();
    setShowBundleBuilder(false);
    setShowBundleList(false);
    setBundleInitialData(null);
    setLeftSelection(null);
    setLeftPaneData([]);
    props.closeSearchProductModal();
  };

  const handleToggleBundleBuilder = () => {
    if (showBundleBuilder) {
      setBundleInitialData(null);
    }
    setShowBundleList(false);
    setShowBundleBuilder((v) => !v);
  };

  const handleToggleBundleList = () => {
    setShowBundleBuilder(false);
    setBundleInitialData(null);
    setShowBundleList((v) => !v);
  };

  /* after a bundle is created, jump to the listing so the user sees it */
  const handleBundleCreated = () => {
    setShowBundleBuilder(false);
    setBundleInitialData(null);
    setShowBundleList(true);
  };

  /* ── saved bundle actions ── */
  const handleLoadSavedBundle = (bundle) => {
    setBundleInitialData({
      title: bundle.title,
      items: bundle.items,
      mrp: bundle.mrp,
      qty: bundle.qty,
    });
    setShowBundleBuilder(true);
  };

  const handleDeleteSavedBundle = (id, e) => {
    e.stopPropagation();
    const updated = loadSavedBundles().filter((b) => b.id !== id);
    persistBundles(updated);
    setSavedBundles(updated);
  };

  return (
    <Modal open={props.searchOpen} onClose={handleDrawerClose} className="new-search-dialog">
      <div className="product-search-drawer-container">
        <div className="product-search-drawer-content">
          {/* ── Header ── */}
          <div className="product-search-header">
            <div className="product-search-header-inner">
              <h2 className="product-search-sr-only">Product search drawer</h2>
              <div className="product-search-header-actions">
                {/* Close */}
                <button
                  type="button"
                  className="product-search-close-btn"
                  onClick={handleDrawerClose}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="m15 9-6 6" />
                    <path d="m9 9 6 6" />
                  </svg>
                </button>

                {/* Search input */}
                <div className="product-search-input-wrapper">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="product-search-icon"
                    aria-hidden="true"
                  >
                    <path d="m21 21-4.34-4.34" />
                    <circle cx="11" cy="11" r="8" />
                  </svg>
                  <input
                    id="search-input"
                    className="product-search-input"
                    placeholder="Search products here..."
                    onChange={handleChange}
                    autoFocus
                    value={searchText}
                    ref={searchRef}
                    disabled={showBundleBuilder || showBundleList}
                    onKeyDown={(e) => {
                      if (e.key === "Tab" && !showBundleBuilder && !showBundleList) {
                        /* Jump focus to the first available ADD button in search results */
                        const firstAddBtn = document.querySelector(
                          ".product-search-add-btn:not([disabled]), .pg-card-add-btn:not([disabled])"
                        );
                        if (firstAddBtn) {
                          e.preventDefault();
                          firstAddBtn.focus();
                        }
                      }
                    }}
                  />
                </div>

                {/* Right-side actions */}
                {loader ? (
                  <CircularProgress size={26} thickness={5} style={{ color: "#64748b" }} />
                ) : (
                  <div className="product-search-header-btns">
                    {!showBundleBuilder && !showBundleList && (
                      <button
                        type="button"
                        className="product-search-clear-btn"
                        onClick={clearSearchText}
                      >
                        Clear
                      </button>
                    )}
                    {leftSelection?.type === "bundles" && !showBundleBuilder && (
                      <button
                        type="button"
                        className="product-search-bundle-btn"
                        onClick={() => {
                          setLeftSelection({ type: "top-searches" });
                          setLeftPaneData(recentData || []);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m15 18-6-6 6-6" />
                        </svg>
                        Back to search
                      </button>
                    )}
                    <button
                      type="button"
                      className={`product-search-bundle-btn${showBundleBuilder ? " active" : ""}`}
                      onClick={handleToggleBundleBuilder}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                        <polyline points="3.29 7 12 12 20.71 7" />
                        <line x1="12" y1="22" x2="12" y2="12" />
                      </svg>
                      {showBundleBuilder ? "Back to search" : "Add bundle"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Body: left sidebar + right content ── */}
          <div
            className={`product-search-results-container${
              showBundleBuilder || leftSelection?.type === "bundles" ? " bundle-active" : ""
            }`}
          >
            {/* Left navigation rail */}
            <SearchSidebar selected={leftSelection} onSelect={handleLeftSelect} />

            {/* Right content pane */}
            <div
              className={`product-search-results-inner${
                showBundleBuilder || (leftSelection?.type === "bundles" && !showBundleBuilder)
                  ? " bundle-mode"
                  : ""
              }`}
            >
              {showBundleBuilder ? (
                <BundleBuilder
                  onCancel={handleBundleBuilderClose}
                  onAddedToCart={handleBundleAddedToCart}
                  onBundleSaved={refreshSavedBundles}
                  onBundleCreated={handleBundleCreated}
                  locId={locId}
                  retailType={retailType}
                  initialData={bundleInitialData}
                />
              ) : leftSelection?.type === "bundles" ? (
                /* onAddedToCart intentionally left as no-op:
                   BundleList now shows inline flash feedback and the operator
                   stays on the product lookup screen to keep adding bundles. */
                <BundleList locId={locId} onAddedToCart={() => {}} />
              ) : leftSelection?.type === "top-searches" ? (
                <>
                  <div className="ps-scan-hint">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                      <line x1="7" y1="12" x2="17" y2="12" />
                    </svg>
                    <span>Scan a barcode or search for a product</span>
                  </div>
                  <ProductGrid
                    products={leftPaneData}
                    title="Top Searches"
                    emptyText="No top searches yet"
                    closeModal={handleDrawerClose}
                  />
                </>
              ) : leftSelection?.type === "category" ? (
                leftPaneLoading ? (
                  <div className="ps-left-loading">
                    <CircularProgress size={24} sx={{ color: "#2a71ff" }} />
                    <span>Loading {leftSelection.name}…</span>
                  </div>
                ) : (
                  <ProductGrid
                    products={leftPaneData}
                    title={leftSelection.name}
                    emptyText={`No products in "${leftSelection.name}"`}
                    closeModal={handleDrawerClose}
                  />
                )
              ) : (
                <>
                  {/* ── Saved bundles strip ── */}
                  {savedBundles.length > 0 && (
                    <div className="saved-bundles-section">
                      <div className="saved-bundles-header">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                          <polyline points="3.29 7 12 12 20.71 7" />
                          <line x1="12" y1="22" x2="12" y2="12" />
                        </svg>
                        <span className="saved-bundles-label">Saved bundles</span>
                        <span className="saved-bundles-count">{savedBundles.length}</span>
                      </div>
                      <div className="saved-bundles-row">
                        {savedBundles.map((bundle) => (
                          <button
                            key={bundle.id}
                            type="button"
                            className="saved-bundle-card"
                            onClick={() => handleLoadSavedBundle(bundle)}
                            title={`Load "${bundle.title}" — ${bundle.items.length} item${
                              bundle.items.length !== 1 ? "s" : ""
                            }`}
                          >
                            <div className="saved-bundle-icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" />
                                <path d="m7 16.5-4.74-2.85" />
                                <path d="m7 16.5 5-3" />
                                <path d="M7 16.5v5.17" />
                                <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" />
                                <path d="m17 16.5-5-3" />
                                <path d="m17 16.5 4.74-2.85" />
                                <path d="M17 16.5v5.17" />
                                <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" />
                                <path d="M12 8 7.26 5.15" />
                                <path d="m12 8 4.74-2.85" />
                                <path d="M12 13.5V8" />
                              </svg>
                            </div>
                            <div className="saved-bundle-info">
                              <span className="saved-bundle-name">{bundle.title}</span>
                              <span className="saved-bundle-meta">
                                {bundle.items.length} item{bundle.items.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                            <span
                              role="button"
                              tabIndex={0}
                              className="saved-bundle-delete"
                              onClick={(e) => handleDeleteSavedBundle(bundle.id, e)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleDeleteSavedBundle(bundle.id, e)
                              }
                              title="Remove saved bundle"
                              aria-label={`Remove ${bundle.title}`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                              </svg>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Search results / top products ── */}
                  <KeyboardDropDownModal
                    closeSearchProductModal={props?.closeSearchProductModal}
                    data={data}
                    listType={listType}
                    onFocusSidebar={focusSidebar}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default memo(SearchBar);
